import {CourseLateEntryPricingMode} from "../../generated/graphql";

export enum TenantLateEntryPricingPolicyValue {
    OriginalPrice = "ORIGINAL_PRICE",
    RemainingLessons = "REMAINING_LESSONS",
    Individual = "INDIVIDUAL",
}

// Dasselbe Enum wie im GraphQL-Schema: ein eigenes Enum mit gleichen Werten wäre den
// Inputs NewCourseInput/EditCourseInput nicht zuweisbar.
export const CourseLateEntryPricingModeValue = CourseLateEntryPricingMode;
export type CourseLateEntryPricingModeValue = CourseLateEntryPricingMode;

export enum CourseMinimumLessonCountSource {
    TenantDefault = "TENANT_DEFAULT",
    CourseOverride = "COURSE_OVERRIDE",
}

export type CourseLateEntryPricingFormFields = {
    lateEntryPricingMode?: CourseLateEntryPricingModeValue | null;
    minimumPartialBookingLessonCount?: number | null;
    /** Reiner Form-State; wird niemals an GraphQL gesendet. */
    minimumPartialBookingLessonCountSource?: CourseMinimumLessonCountSource;
};

export type CourseLateEntryPricingPayload = {
    lateEntryPricingMode: CourseLateEntryPricingModeValue;
    minimumPartialBookingLessonCount: number | null;
};

export type CourseLateEntryPricingInitialValues =
    CourseLateEntryPricingPayload & {
        minimumPartialBookingLessonCountSource: CourseMinimumLessonCountSource;
    };

export const COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR =
    "Bitte eine ganze Zahl größer oder gleich 1 eingeben.";

export const isTenantLateEntryPricingPolicy = (
    value: unknown,
): value is TenantLateEntryPricingPolicyValue =>
    value === TenantLateEntryPricingPolicyValue.OriginalPrice ||
    value === TenantLateEntryPricingPolicyValue.RemainingLessons ||
    value === TenantLateEntryPricingPolicyValue.Individual;

export const isCourseLateEntryPricingMode = (
    value: unknown,
): value is CourseLateEntryPricingModeValue =>
    value === CourseLateEntryPricingModeValue.OriginalPrice ||
    value === CourseLateEntryPricingModeValue.RemainingLessons;

export const isCourseMinimumLessonCountSource = (
    value: unknown,
): value is CourseMinimumLessonCountSource =>
    value === CourseMinimumLessonCountSource.TenantDefault ||
    value === CourseMinimumLessonCountSource.CourseOverride;

export const isValidMinimumPartialBookingLessonCount = (
    value: unknown,
): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value >= 1;

/**
 * Legacy courses have neither field. They deliberately fall back to the
 * original price and inherit the tenant minimum until an admin opts in.
 */
export const getCourseLateEntryPricingInitialValues = (
    values: {
        lateEntryPricingMode?: unknown;
        minimumPartialBookingLessonCount?: unknown;
    },
): CourseLateEntryPricingInitialValues => {
    const minimum = isValidMinimumPartialBookingLessonCount(
        values.minimumPartialBookingLessonCount,
    )
        ? values.minimumPartialBookingLessonCount
        : null;

    return {
        lateEntryPricingMode: isCourseLateEntryPricingMode(
            values.lateEntryPricingMode,
        )
            ? values.lateEntryPricingMode
            : CourseLateEntryPricingModeValue.OriginalPrice,
        minimumPartialBookingLessonCount: minimum,
        minimumPartialBookingLessonCountSource:
            minimum === null
                ? CourseMinimumLessonCountSource.TenantDefault
                : CourseMinimumLessonCountSource.CourseOverride,
    };
};

/**
 * Builds the exact GraphQL input fragment shared by create, regular edit and
 * allowLessonBooking edit. `null` is intentional: it means tenant default.
 */
export const buildCourseLateEntryPricingPayload = (
    values: CourseLateEntryPricingFormFields,
): CourseLateEntryPricingPayload => {
    const lateEntryPricingMode = isCourseLateEntryPricingMode(
        values.lateEntryPricingMode,
    )
        ? values.lateEntryPricingMode
        : CourseLateEntryPricingModeValue.OriginalPrice;
    const minimum = values.minimumPartialBookingLessonCount;
    const minimumSource = isCourseMinimumLessonCountSource(
        values.minimumPartialBookingLessonCountSource,
    )
        ? values.minimumPartialBookingLessonCountSource
        : minimum === null || typeof minimum === "undefined"
        ? CourseMinimumLessonCountSource.TenantDefault
        : CourseMinimumLessonCountSource.CourseOverride;

    if (minimumSource === CourseMinimumLessonCountSource.TenantDefault) {
        return {
            lateEntryPricingMode,
            minimumPartialBookingLessonCount: null,
        };
    }

    if (!isValidMinimumPartialBookingLessonCount(minimum)) {
        throw new Error(COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR);
    }

    return {
        lateEntryPricingMode,
        minimumPartialBookingLessonCount: minimum,
    };
};
